import React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { CommunityDemographics } from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";
import SectionShell from "../SectionShell";
import {
  SectionEmptyState,
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import {
  countryCodeToFlagEmoji,
  resolveCountryName,
} from "../utils";

interface GlobalPresenceProps {
  query: UseQueryResult<CommunityDemographics>;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
  regionDisplayNames: Intl.DisplayNames | null;
}

/**
 * Public geography distribution for the community dashboard.
 */
const GlobalPresence: React.FC<GlobalPresenceProps> = ({
  query,
  labels,
  numberFormatter,
  regionDisplayNames,
}) => {
  return (
    <SectionShell
      title={labels.globalPresenceTitle}
      subtitle={labels.globalPresenceSubtitle}
      contentClassName="space-y-4"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data && (query.data.buckets.length > 0 || query.data.users_unknown_country > 0) ? (
        (() => {
          const maxCount = Math.max(
            ...query.data.buckets.map((bucket) => bucket.user_count),
            query.data.users_unknown_country,
            1
          );

          return (
            <>
              <div className="flex flex-wrap gap-6 rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/25 px-4 py-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div>
                  <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {labels.globalPublicCountryUsers}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {numberFormatter.format(query.data.users_with_public_country)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {labels.countryUnknown}
                  </p>
                  <p className="mt-2 text-3xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                    {numberFormatter.format(query.data.users_unknown_country)}
                  </p>
                </div>
              </div>
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{labels.demographicsNote}</p>
              <div className="space-y-3">
                {query.data.buckets.map((bucket) => {
                  const widthPct = Math.round((bucket.user_count / maxCount) * 100);
                  const countryName = resolveCountryName(
                    bucket.country_code,
                    regionDisplayNames,
                    labels.countryUnknown
                  );
                  const flagEmoji = countryCodeToFlagEmoji(bucket.country_code);

                  return (
                    <div key={bucket.country_code} className="space-y-1">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="min-w-0 truncate font-medium text-eoe-text dark:text-eoe-dark-text">
                          {flagEmoji ? `${flagEmoji} ${countryName}` : countryName}
                        </span>
                        <span className="tabular-nums text-eoe-text-sub dark:text-eoe-dark-text-sub">
                          {numberFormatter.format(bucket.user_count)}
                        </span>
                      </div>
                      <div
                        className="h-3 overflow-hidden rounded-full bg-eoe-border/70 dark:bg-eoe-dark-border/70"
                        role="meter"
                        aria-valuemin={0}
                        aria-valuemax={maxCount}
                        aria-valuenow={bucket.user_count}
                        aria-label={countryName}
                      >
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-600 via-cyan-500 to-violet-500"
                          style={{ width: `${widthPct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          );
        })()
      ) : (
        <SectionEmptyState title={labels.emptyDemographics} />
      )}
    </SectionShell>
  );
};

export default GlobalPresence;

